import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, MapPin, FileCheck, Newspaper } from 'lucide-react';
import { fetchDestinations, fetchVisas, fetchBlogPosts, resolveImageUrl } from '../lib/travelApi';

// Compact site search overlay: searches Destinations, Visas and Blog posts.
const SearchOverlay = ({ onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [data, setData] = useState({ destinations: [], visas: [], blog: [] });
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    Promise.all([
      fetchDestinations().catch(() => []),
      fetchVisas().catch(() => []),
      fetchBlogPosts().catch(() => []),
    ]).then(([destinations, visas, blog]) => setData({ destinations, visas, blog }));

    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const go = useCallback((path) => { onClose(); navigate(path); }, [navigate, onClose]);

  const q = query.trim().toLowerCase();
  const dest = q ? data.destinations.filter((d) => d.name?.toLowerCase().includes(q)) : [];
  const visas = q ? data.visas.filter((v) => v.country?.toLowerCase().includes(q)) : [];
  const blog = q ? data.blog.filter((b) => b.title?.toLowerCase().includes(q)) : [];
  const hasResults = dest.length || visas.length || blog.length;

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-24 px-4" onClick={onClose} data-testid="search-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search className="w-5 h-5 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            data-testid="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search destinations, visas, blog..."
            className="flex-1 outline-none text-gray-800 text-lg"
          />
          <button onClick={onClose} data-testid="search-close" className="p-1 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {!q ? (
            <p className="p-6 text-center text-gray-400 text-sm">Type to search across destinations, visa services and travel blog.</p>
          ) : !hasResults ? (
            <p className="p-6 text-center text-gray-500 text-sm">No matches for "{query}".</p>
          ) : (
            <div className="py-2">
              {dest.length > 0 && (
                <div>
                  <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Destinations</p>
                  {dest.map((d) => (
                    <button key={d.id} data-testid={`search-result-dest-${d.id}`} onClick={() => go(`/destination/${d.id}/activities`)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-amber-50 text-left">
                      <img src={resolveImageUrl(d.image)} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                      <span className="text-gray-800">{d.name}</span>
                      <MapPin className="w-4 h-4 text-amber-500 ml-auto" />
                    </button>
                  ))}
                </div>
              )}
              {visas.length > 0 && (
                <div>
                  <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Visa Services</p>
                  {visas.map((v) => (
                    <button key={v.id} data-testid={`search-result-visa-${v.id}`} onClick={() => go('/visa')} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-amber-50 text-left">
                      <FileCheck className="w-5 h-5 text-amber-600 shrink-0" />
                      <span className="text-gray-800">{v.country} Visa</span>
                      {v.price > 0 && <span className="ml-auto text-sm text-amber-600 font-semibold">~₹{Number(v.price).toLocaleString('en-IN')}</span>}
                    </button>
                  ))}
                </div>
              )}
              {blog.length > 0 && (
                <div>
                  <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Travel Blog</p>
                  {blog.map((b) => (
                    <button key={b.id} data-testid={`search-result-blog-${b.slug}`} onClick={() => go(`/blog/${b.slug}`)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-amber-50 text-left">
                      <Newspaper className="w-5 h-5 text-gray-500 shrink-0" />
                      <span className="text-gray-800">{b.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
